import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import type { DocumentHeading } from "./headings";

interface TableOfContentsProps {
  headings: DocumentHeading[];
  /** Jump to the heading at this index among the rendered h2–h4 elements. */
  onJump: (index: number) => void;
}

const OutlineNav = styled("nav")(({ theme }) => ({
  display: "none",
  [theme.breakpoints.up("md")]: { display: "block" },
  position: "sticky",
  top: 0,
  alignSelf: "flex-start",
  flexShrink: 1,
  maxWidth: 250,
  maxHeight: "100%",
  overflowY: "auto",
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const OutlineLabel = styled("p")(({ theme }) => ({
  color: theme.palette.text.secondary,
  paddingLeft: theme.spacing(1),
  paddingRight: theme.spacing(1),
  display: "block",
  marginBottom: theme.spacing(0.5),
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.08333em",
  textTransform: "uppercase",
  lineHeight: 2.66,
  margin: 0,
  fontFamily: "inherit",
}));

const HeadingButton = styled("button")<{ $indent: number }>(({ theme, $indent }) => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "none",
  border: "none",
  borderLeft: "2px solid transparent",
  cursor: "pointer",
  color: theme.palette.text.secondary,
  fontFamily: theme.typography.fontFamily,
  fontSize: theme.typography.body2.fontSize,
  paddingLeft: theme.spacing(1 + $indent * 1.5),
  paddingRight: theme.spacing(1),
  paddingTop: "3.2px",
  paddingBottom: "3.2px",
  borderRadius: `${theme.shape.borderRadius}px`,
  lineHeight: 1.3,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  "&:hover": {
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.action.hover,
    borderLeftColor: theme.palette.primary.main,
  },
}));

/**
 * Sticky document-outline rail: one clickable entry per section heading, indented by level.
 * Rendered to the left of the document sheet; entry index maps to the rendered heading's index.
 */
export function TableOfContents({ headings, onJump }: TableOfContentsProps) {
  return (
    <OutlineNav aria-label="Document outline">
      <OutlineLabel>Contents</OutlineLabel>
      <Stack>
        {headings.map((heading, index) => (
          <HeadingButton
            key={`${index}-${heading.text}`}
            onClick={() => onJump(index)}
            title={heading.text}
            $indent={heading.level - 2}
          >
            {heading.text}
          </HeadingButton>
        ))}
      </Stack>
    </OutlineNav>
  );
}
